'use client'

import { ArrowUp, AlertCircle, Loader2, Maximize2, MessageCircle, Minimize2, ShoppingCart, User, X } from 'lucide-react'
import moment from 'moment'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { useCustomerChat } from '@/lib/commerce/hooks'
import { formatImageURL, formatPrice } from '@/utils/util'
import { CHAT_MAX_MESSAGE_LENGTH, CHAT_SUGGESTED_QUESTIONS, CHAT_PAYMENT_LABELS, parseChatMessageContent, getChatAlternativePayments, getChatProductDisplay } from '@/utils/chat'
import { useIsMobile } from '@/hooks/use-mobile'
import { useScrollVisibility } from '@/hooks/use-scroll-visibility'
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock'
import type { CustomerChatMessage } from '@/types/customerChat'
import Logo from '@/assets/favicon.png'

const surfaceStyle: React.CSSProperties = { background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }
const insetStyle: React.CSSProperties = { background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.07)' }

function BotAvatar({ src, size = 'md' }: { src?: string | null, size?: 'sm' | 'md' }) {
  const px = size === 'sm' ? 32 : 40
  const inner = size === 'sm' ? 14 : 18
  return (
    <div
      className="rounded-full overflow-hidden grid place-items-center shrink-0"
      style={{ width: px, height: px, background: 'linear-gradient(135deg, #2a2a30, #08080a)', border: '1px solid rgba(255,255,255,0.14)' }}
    >
      {src ? (
        <img src={formatImageURL(src)} alt="Bot Avatar" className="w-full h-full object-cover" />
      ) : (
        <Image src={Logo} alt="Bot Avatar" style={{ width: inner, height: inner }} />
      )}
    </div>
  )
}

export default function CustomerChat({ vendor, botName, botAvatar }: { vendor: any, botName?: string, botAvatar?: string }) {
  const isMobile = useIsMobile()
  const pathname = usePathname()
  const isNavVisible = useScrollVisibility()
  const c = useCustomerChat({ vendor, defaultBotName: botName, defaultBotAvatar: botAvatar })

  useBodyScrollLock(!!isMobile && c.isChatOpen)

  const isProductDetailPage = /\/product\/[^/]+/.test(pathname || '')
  const mobileBottomClass = isProductDetailPage ? 'bottom-36' : 'bottom-20'
  const positionClasses = c.chatPosition === 'bottom-left'
    ? `${mobileBottomClass} left-4 lg:bottom-6 lg:left-6`
    : `${mobileBottomClass} right-4 lg:bottom-6 lg:right-6`

  const renderProductList = (products: any[]) => (
    <div className="space-y-3 max-w-72">
      {products.map((product) => {
        const { hasMultipleVariants, selectedVariantId, selectedVariant, displayHasDiscount, displayImage, displayStock, displayName, isTrackable, isInStock } = getChatProductDisplay(product, c.selectedVariants[product.id])
        return (
          <div key={product.id} className="rounded-xl overflow-hidden" style={surfaceStyle}>
            <div className="relative w-full aspect-square" style={{ background: 'rgba(0,0,0,0.25)' }}>
              <img src={formatImageURL(displayImage)} alt={product.name} className="w-full h-full object-cover" />
              {displayHasDiscount && (
                <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest" style={{ background: 'white', color: '#0a0a0c' }}>
                  -{product.discountPercent}%
                </span>
              )}
            </div>
            <div className="p-3 space-y-2.5">
              <div className="text-[13px] font-semibold text-white line-clamp-2 leading-snug" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
                {product.name}
                {product.isDigital && <span className="ml-1 text-[11px] font-normal" style={{ color: '#93c5fd' }}>📩 Дижитал</span>}
              </div>
              {hasMultipleVariants ? (
                <select
                  value={selectedVariantId || ''}
                  onChange={(e) => c.handleVariantChange(product.id, e.target.value)}
                  className="w-full h-9 rounded-lg px-3 text-[12px] outline-none focus:ring-0 appearance-none"
                  style={{ ...insetStyle, color: 'white' }}
                >
                  {product.variants.map((variant: any) => (
                    <option key={variant.id} value={variant.id} style={{ background: '#0b0b0e' }}>
                      {variant.name}{isTrackable && variant.stock != null && variant.stock > 0 ? ` (${variant.stock} ш.)` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="flex items-center justify-between text-[11px]">
                  <span style={{ color: 'rgba(255,255,255,0.55)' }}>{displayName}</span>
                  {isTrackable && displayStock != null && (
                    <span className="font-medium" style={{ color: isInStock ? '#34d399' : '#fda4af' }}>
                      {isInStock ? `${displayStock} ширхэг` : 'Дууссан'}
                    </span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-[14px] font-bold tabular-nums" style={{ color: displayHasDiscount ? '#fda4af' : 'white', fontFamily: 'Gilroy, Inter, sans-serif' }}>
                  {formatPrice(selectedVariant?.price)}
                </span>
                {displayHasDiscount && (
                  <span className="text-[11px] tabular-nums line-through" style={{ color: 'rgba(255,255,255,0.4)' }}>{formatPrice(selectedVariant?.originalPrice)}</span>
                )}
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => c.goToProduct(product.customId)}
                  className="flex-1 h-8 text-[11px] font-medium rounded-full inline-flex items-center justify-center cursor-pointer transition-colors hover:bg-white/10"
                  style={{ background: 'rgba(255,255,255,0.035)', color: 'white', border: '1px solid rgba(255,255,255,0.07)' }}
                >
                  Дэлгэрэнгүй
                </button>
                <button
                  type="button"
                  onClick={() => c.handleAddToCart(product, selectedVariantId)}
                  disabled={!isInStock || c.loading}
                  className="flex-1 h-8 text-[11px] font-semibold rounded-full inline-flex items-center justify-center gap-1.5 cursor-pointer transition-opacity disabled:opacity-60"
                  style={{ background: 'white', color: '#0a0a0c' }}
                >
                  <ShoppingCart className="h-3 w-3" /> Сагслах
                </button>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )

  const renderOrder = (metadata: any) => {
    const alternativePayments = getChatAlternativePayments(metadata, c.configMethods)
    return (
      <div className="space-y-3">
        <div className="text-[13px] space-y-1 text-white">
          <div>📋 Дугаар: <span className="font-semibold tabular-nums">{metadata.customId}</span></div>
          <div>💰 Дүн: <span className="font-semibold tabular-nums">{formatPrice(metadata.amount)}</span></div>
        </div>
        <button
          type="button"
          onClick={() => c.goToOrder(metadata.customId, metadata.phone)}
          className="w-full h-9 rounded-full text-[12px] font-semibold inline-flex items-center justify-center cursor-pointer"
          style={{ background: 'white', color: '#0a0a0c' }}
        >
          {CHAT_PAYMENT_LABELS[metadata.paymentMethod] || '📦 Захиалга харах'}
        </button>
        {alternativePayments.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-[11px]" style={{ color: 'rgba(255,255,255,0.55)' }}>Төлбөрийн хэлбэр солих:</div>
            <div className="flex flex-wrap gap-1">
              {alternativePayments.map(p => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => c.handleSuggestedQuestion(p.chatLabel)}
                  className="cursor-pointer py-2 px-3 text-left text-[11px] rounded-lg transition-colors hover:bg-white/5"
                  style={{ ...surfaceStyle, color: 'rgba(255,255,255,0.85)' }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderCart = (metadata: any) => {
    const { items, dealItems, subTotal } = metadata
    return (
      <div className="space-y-2 text-white">
        <ul className="space-y-1.5">
          {items.map((item: any) => (
            <li key={item.id} className="text-[13px]">
              {item.name}
              {item.isDigital && <span style={{ color: '#93c5fd' }}> 📩</span>}
              <span style={{ color: 'rgba(255,255,255,0.6)' }}> x{item.quantity}</span> — <span className="tabular-nums">{formatPrice(item.total)}</span>
            </li>
          ))}
        </ul>
        {dealItems && dealItems.length > 0 && (
          <div className="text-[11px]" style={{ color: 'rgba(255,255,255,0.55)' }}>🎉 Таны сагсанд {dealItems.length} хямдралтай бараа автоматаар нэмэгдсэн байна!</div>
        )}
        <div className="font-bold pt-2 tabular-nums" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>Нийт: {formatPrice(subTotal)}</div>
      </div>
    )
  }

  const renderOrderTracking = (metadata: any) => (
    <button
      type="button"
      onClick={() => c.goToOrder(metadata.customId, metadata.phone)}
      className="w-full h-9 rounded-full text-[12px] font-semibold inline-flex items-center justify-center cursor-pointer"
      style={{ background: 'white', color: '#0a0a0c' }}
    >
      📦 Захиалга харах
    </button>
  )

  const renderMessage = (msg: CustomerChatMessage) => {
    const isUser = msg.senderRole === 'user'
    const isSystem = msg.senderRole === 'system'

    if (isSystem) {
      return (
        <div key={msg.id} className="flex justify-center my-2">
          <div
            className="px-3 py-1.5 rounded-full text-[11px]"
            style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            {msg.message}
          </div>
        </div>
      )
    }

    const parsedContent = !isUser ? parseChatMessageContent(msg.message) : null

    return (
      <div key={msg.id} className={`flex gap-2 mb-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
        {isUser ? (
          <div className="h-8 w-8 rounded-full grid place-items-center shrink-0" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <User className="h-4 w-4" style={{ color: 'rgba(255,255,255,0.7)' }} />
          </div>
        ) : (
          <BotAvatar src={c.displayBotAvatar} size="sm" />
        )}
        <div className={`flex flex-col ${isUser ? 'max-w-[85%] items-end' : 'max-w-[90%] items-start'}`}>
          <div
            className={`rounded-2xl ${parsedContent ? 'p-3' : 'px-3 py-2'}`}
            style={
              isUser
                ? { background: 'white', color: '#0a0a0c' }
                : { background: 'rgba(255,255,255,0.06)', color: 'white', border: '1px solid rgba(255,255,255,0.07)' }
            }
          >
            {parsedContent ? (
              <div className="space-y-2">
                {parsedContent.type !== 'order' && <div className="text-[13px]" dangerouslySetInnerHTML={{ __html: parsedContent.message || '' }} />}
                {parsedContent.type === 'product_list' && renderProductList(parsedContent.metadata.products)}
                {parsedContent.type === 'cart' && renderCart(parsedContent.metadata)}
                {parsedContent.type === 'order' && renderOrder(parsedContent.metadata)}
                {parsedContent.type === 'order_tracking' && renderOrderTracking(parsedContent.metadata)}
              </div>
            ) : (
              <div className="text-[13px] wrap-anywhere whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: msg.message }} />
            )}
          </div>
          <span className="text-[10px] mt-1 tabular-nums" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {moment(msg.sentAt).format('HH:mm')}
          </span>
        </div>
      </div>
    )
  }

  if (!c.isMounted) return null

  if (!c.isChatOpen) {
    return (
      <button
        onClick={() => c.setIsChatOpen(true)}
        aria-label="Chat"
        className={`fixed cursor-pointer ${positionClasses} z-50 h-14 w-14 rounded-full shadow-2xl transition-all duration-300 flex items-center justify-center hover:scale-105 ${!isNavVisible ? 'translate-y-16 lg:translate-y-0' : 'translate-y-0'}`}
        style={{ background: 'white', color: '#0a0a0c', boxShadow: '0 16px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.13)' }}
      >
        <MessageCircle className="h-6 w-6" strokeWidth={2} />
        {!c.isConnected && (
          <span className="absolute top-0 right-0 h-3 w-3 rounded-full" style={{ background: '#fda4af', border: '2px solid #08080a' }} />
        )}
      </button>
    )
  }

  const panelStateClasses = isMobile
    ? 'top-0 left-0 right-0 w-full h-dvh rounded-none'
    : c.isChatExpanded
      ? `top-2 ${c.chatPosition === 'bottom-left' ? 'left-2' : 'right-2'} h-[calc(100vh-1rem)] w-100 rounded-2xl`
      : `${positionClasses} w-95 h-150 rounded-2xl`

  return (
    <div
      className={`fixed z-50 flex flex-col transition-all duration-300 ${panelStateClasses}`}
      style={{
        background: '#0b0b0e',
        border: '1px solid rgba(255,255,255,0.13)',
        boxShadow: '0 30px 80px -20px rgba(0,0,0,0.7)',
      }}
    >
      <div
        className={`flex items-center justify-between shrink-0 ${isMobile ? 'rounded-none' : 'rounded-t-2xl'} px-4 py-3`}
        style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(8,8,10,0.95)' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <BotAvatar src={c.displayBotAvatar} size="md" />
          <div className="min-w-0">
            <div className="text-[14px] font-semibold text-white truncate" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{c.displayBotName}</div>
            <div className="text-[11px] flex items-center gap-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>
              {c.isConnected && (
                <span className="relative flex w-2 h-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-400" />
                </span>
              )}
              {c.isConnected ? 'Онлайн' : 'Холбогдож байна...'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!isMobile && (
            <button
              type="button"
              onClick={() => c.setIsChatExpanded(!c.isChatExpanded)}
              aria-label={c.isChatExpanded ? 'Багасгах' : 'Томруулах'}
              className="w-8 h-8 rounded-full inline-flex items-center justify-center transition-colors hover:bg-white/5 cursor-pointer"
              style={{ color: 'rgba(255,255,255,0.6)' }}
            >
              {c.isChatExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
          )}
          <button
            type="button"
            onClick={c.handleClose}
            aria-label="Хаах"
            className="w-8 h-8 rounded-full inline-flex items-center justify-center transition-colors hover:bg-white/5 cursor-pointer"
            style={{ color: 'rgba(255,255,255,0.6)' }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain scrollbar-dark p-4">
        {!c.currentSession && c.isLoading ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <Loader2 className="h-7 w-7 animate-spin" style={{ color: 'rgba(255,255,255,0.55)' }} />
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.55)' }}>Чат эхлүүлж байна...</p>
          </div>
        ) : !c.isConnected ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
            <AlertCircle className="h-8 w-8" style={{ color: '#fda4af' }} />
            <p className="text-[13px] font-medium" style={{ color: '#fda4af' }}>Холбогдож чадсангүй</p>
            <p className="text-[12px] px-4" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Интернэт холболтоо шалгаад дахин оролдоно уу
            </p>
            {c.error && (
              <p className="text-[12px] text-center px-3 py-2 rounded-lg" style={{ ...surfaceStyle, color: 'rgba(255,255,255,0.6)' }}>
                {c.error}
              </p>
            )}
          </div>
        ) : c.error ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
            <AlertCircle className="h-8 w-8" style={{ color: '#fda4af' }} />
            <p className="text-[13px]" style={{ color: '#fda4af' }}>{c.error}</p>
            <button
              type="button"
              onClick={c.handleStartChat}
              className="h-9 px-4 rounded-full text-[12px] font-semibold cursor-pointer"
              style={{ background: 'white', color: '#0a0a0c' }}
            >
              Дахин оролдох
            </button>
          </div>
        ) : c.messages.length <= 1 ? (
          <div className="flex flex-col h-full">
            <div className="space-y-1 flex-1">
              {c.messages.map((msg) => renderMessage(msg))}
            </div>
            <div className="flex flex-wrap gap-1.5 pt-4">
              {CHAT_SUGGESTED_QUESTIONS.map((question, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => c.handleSuggestedQuestion(question)}
                  disabled={!c.currentSession || c.isLoading}
                  className="cursor-pointer py-2 px-3 text-left text-[11.5px] rounded-lg transition-colors hover:bg-white/5 disabled:opacity-50"
                  style={{ ...surfaceStyle, color: 'rgba(255,255,255,0.85)' }}
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            {c.messages.map((msg) => renderMessage(msg))}
            {c.isAiTyping && (
              <div className="flex gap-2 mb-3">
                <BotAvatar src={c.displayBotAvatar} size="sm" />
                <div className="px-3 py-2 rounded-2xl" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="flex gap-1 mt-1">
                    <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: 'rgba(255,255,255,0.5)' }} />
                    <span className="w-1.5 h-1.5 rounded-full animate-bounce [animation-delay:0.2s]" style={{ background: 'rgba(255,255,255,0.5)' }} />
                    <span className="w-1.5 h-1.5 rounded-full animate-bounce [animation-delay:0.4s]" style={{ background: 'rgba(255,255,255,0.5)' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={c.messagesEndRef} />
          </div>
        )}
      </div>

      <form onSubmit={c.handleSendMessage} className="px-3 py-3 shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="rounded-2xl p-2.5" style={insetStyle}>
          <textarea
            placeholder="Асуултаа бичнэ үү..."
            value={c.inputMessage}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => c.handleInputChange(e.target.value)}
            disabled={!c.currentSession || c.isLoading}
            rows={c.textareaRows}
            className="w-full bg-transparent text-[13px] text-white border-0 outline-none resize-none focus:outline-none focus:ring-0 placeholder:text-white/30 max-h-18 min-h-0"
            onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                c.handleSendMessage(e)
              }
            }}
          />
          <div className="flex items-center justify-between gap-2 mt-1">
            <span
              className="text-[10px] tabular-nums"
              style={{ color: c.inputMessage.length > CHAT_MAX_MESSAGE_LENGTH ? '#fda4af' : 'rgba(255,255,255,0.4)' }}
            >
              {c.inputMessage.length}/{CHAT_MAX_MESSAGE_LENGTH}
            </span>
            <button
              type="submit"
              disabled={!c.inputMessage.trim() || !c.currentSession || c.isLoading || c.inputMessage.length > CHAT_MAX_MESSAGE_LENGTH}
              aria-label="Илгээх"
              className="w-8 h-8 rounded-full inline-flex items-center justify-center transition-opacity disabled:opacity-40 cursor-pointer"
              style={{ background: 'white', color: '#0a0a0c' }}
            >
              <ArrowUp className="h-4 w-4" strokeWidth={2.25} />
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
